import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  FormGroup,
  Chip,
  Divider,
  Alert,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";
import currencyData from "src/constants/currencyData.json";

// Constants
const PRIMARY_COLOR = "#6200EE";
const PRIMARY_LIGHT = "#F2E9FF";
const TRANSITION = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";


// Styled Components
const CustomOverlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "800px",
  height: "600px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "75vh",
    borderRadius: 12,
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "12px 0",
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
  [theme.breakpoints.down("sm")]: { padding: "8px 0" },
  [theme.breakpoints.down(400)]: { padding: "6px 0" },
}));

const BottomActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 16,
  paddingTop: "16px",
  borderTop: "2px solid #E2E8F0",
  marginTop: "auto",
  [theme.breakpoints.down("sm")]: { gap: 12, paddingTop: "12px" },
  [theme.breakpoints.down(400)]: { gap: 8, paddingTop: "10px" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  height: 56,
  borderRadius: 16,
  textTransform: "lowercase",
  fontSize: "1rem",
  fontWeight: 700,
  transition: TRANSITION,
  "&.primary": {
    background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, #7C4DFF 100%)`,
    color: "white",
    boxShadow: `0 8px 25px -8px rgba(98, 0, 238, 0.4)`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 12px 35px -8px rgba(98, 0, 238, 0.6)`,
    },
  },
  "&.secondary": {
    background: PRIMARY_LIGHT,
    color: PRIMARY_COLOR,
    border: "2px solid #E2E8F0",
    "&:hover": {
      backgroundColor: PRIMARY_COLOR,
      color: "white",
      transform: "translateY(-2px)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    height: 48,
    borderRadius: 12,
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down(400)]: {
    height: 44,
    borderRadius: 10,
    fontSize: "0.8rem",
  },
}));

const CancellationDialog = ({ 
  open, 
  onClose, 
  selectedCartItem, 
  orderData,
  onSubmit,
  isLoading = false,
}) => {
  const [cancellationType, setCancellationType] = useState("partial");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [businessApprovedItemIds, setBusinessApprovedItemIds] = useState([]); // Track business-approved cancelled items

  // Currency: order > location, never hardcode USD/$
  const locationCurrency = orderData?.locationId?.currency;
  const locationCurrencySymbol = orderData?.locationId?.currencySymbol;

  const getCurrencySymbol = (currencyCode, fallbackSymbol) => {
    if (!currencyCode || !currencyData?.currencies) return fallbackSymbol ?? "";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : (fallbackSymbol ?? "");
  };

  const formatPrice = (price, currencyCode) => {
    const symbol = getCurrencySymbol(currencyCode || locationCurrency, locationCurrencySymbol);
    const formattedPrice = typeof price === 'number' ? price.toFixed(2) : parseFloat(price || 0).toFixed(2);
    return `${symbol}${formattedPrice}`;
  };

  // Get currency from orderData or location
  const getOrderCurrency = () => {
    return orderData?.currency || locationCurrency || "";
  };

  // Get currency from item (variant items may have currency in items)
  const getItemCurrency = (item) => {
    if (item?.currency) return item.currency;
    return getOrderCurrency();
  };

  // Helper function to get business-approved cancelled item IDs
  // Items with status "approved" OR "pending_approval" are considered business-approved
  // Items with status "rejected" are NOT included
  const getBusinessApprovedItemIds = () => {
    const approvedItemIds = [];
    
    // Get the current variant name (if this is a variant item)
    const currentVariantName = selectedCartItem.variantEnabled && selectedCartItem.variants?.length > 0 
      ? selectedCartItem.variants[0].variantName 
      : null;
    
    if (selectedCartItem.variantEnabled && selectedCartItem.variants) {
      // For variant items - check individual item cancellations
      selectedCartItem.variants.forEach(variant => {
        variant.items?.forEach(item => {
          // Include items with status "approved" OR "pending_approval" (not "rejected")
          if (item.cancellation?.requested && (item.cancellation?.status === "approved" || item.cancellation?.status === "pending_approval")) {
            if (!approvedItemIds.includes(item._id)) {
              approvedItemIds.push(item._id);
            }
          }
        });
      });
    } else {
      // For non-variant items
      selectedCartItem.items?.forEach(item => {
        // Include items with status "approved" OR "pending_approval" (not "rejected")
        if (item.cancellation?.requested && (item.cancellation?.status === "approved" || item.cancellation?.status === "pending_approval")) {
          if (!approvedItemIds.includes(item._id)) {
            approvedItemIds.push(item._id);
          }
        }
      });
    }
    
    // Also check cancellations array if it exists
    // IMPORTANT: For variant items, only check cancellations matching the current variant
    if (selectedCartItem.cancellations && Array.isArray(selectedCartItem.cancellations)) {
      selectedCartItem.cancellations.forEach(cancellation => {
        // Include items with status "approved" OR "pending_approval" (not "rejected")
        if (cancellation.requested && (cancellation.status === "approved" || cancellation.status === "pending_approval") && cancellation.itemIds) {
          // For variant items, only include if variant name matches current variant
          if (currentVariantName) {
            if (cancellation.variantName === currentVariantName) {
              cancellation.itemIds.forEach(itemId => {
                if (!approvedItemIds.includes(itemId)) {
                  approvedItemIds.push(itemId);
                }
              });
            }
          } else {
            // For non-variant items, include all
            cancellation.itemIds.forEach(itemId => {
              if (!approvedItemIds.includes(itemId)) {
                approvedItemIds.push(itemId);
              }
            });
          }
        }
      });
    }
    
    return approvedItemIds;
  };

  useEffect(() => {
    if (open && selectedCartItem) {
      // Get business-approved cancelled items
      const approvedItemIds = getBusinessApprovedItemIds();
      setBusinessApprovedItemIds(approvedItemIds);
      
      // Pre-select items based on EXISTING CUSTOMER CANCELLATIONS (from API data)
      // Exclude business-approved items and rejected items from pre-selection
      // ONLY include items with no status (customer cancellations)
      const preCancelledItemIds = [];
      
      if (selectedCartItem.variantEnabled && selectedCartItem.variants) {
        // For variant items, check cancelled items within the SELECTED variant only
        selectedCartItem.variants.forEach(variant => {
          variant.items?.forEach(item => {
            // ONLY include items that are:
            // 1. requested = true
            // 2. status is undefined/null (customer cancellations only) OR 'local_pending'
            // 3. NOT business-approved (status !== "approved" AND status !== "pending_approval")
            // 4. NOT rejected (status !== "rejected")
            const isCustomerCancellation = item.cancellation?.requested && 
              (!item.cancellation?.status || item.cancellation?.status === 'local_pending');
            
            if (isCustomerCancellation && !approvedItemIds.includes(item._id)) {
              if (!preCancelledItemIds.includes(item._id)) {
                preCancelledItemIds.push(item._id);
              }
            }
          });
        });
      } else {
        // For non-variant items
        selectedCartItem.items?.forEach(item => {
          // ONLY include items that are:
          // 1. requested = true
          // 2. status is undefined/null (customer cancellations only) OR 'local_pending'
          // 3. NOT business-approved (status !== "approved" AND status !== "pending_approval")
          // 4. NOT rejected (status !== "rejected")
          const isCustomerCancellation = item.cancellation?.requested && 
            (!item.cancellation?.status || item.cancellation?.status === 'local_pending');
          
          if (isCustomerCancellation && !approvedItemIds.includes(item._id)) {
            if (!preCancelledItemIds.includes(item._id)) {
              preCancelledItemIds.push(item._id);
            }
          }
        });
      }
      
      setSelectedItemIds(preCancelledItemIds);
      setCancellationType("partial");
      setReason("");
      setErrors({});
    }
  }, [open, selectedCartItem]);

  const validateForm = () => {
    const newErrors = {};
    // Filter out business-approved items from validation
    const validSelectedItems = selectedItemIds.filter(id => !businessApprovedItemIds.includes(id));
    
    if (cancellationType === "partial" && validSelectedItems.length === 0) {
      newErrors.itemIds = "please select at least one item to cancel";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleItemIdToggle = (itemId) => {
    // Prevent toggling business-approved cancelled items
    if (businessApprovedItemIds.includes(itemId)) {
      return;
    }
    
    setSelectedItemIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    let allItemIds = [];
    let totalQuantity = 0;
    let finalCancellationType = cancellationType;

    if (cancellationType === "full") {
      if (selectedCartItem.variantEnabled && selectedCartItem.variants) {
        const variant = selectedCartItem.variants[0];
        // Filter out business-approved items
        allItemIds = variant.items?.map((item) => item._id).filter(id => !businessApprovedItemIds.includes(id)) || [];
        totalQuantity =
          variant.items?.reduce(
            (total, item) => total + (item.quantity || 0),
            0
          ) || 0;
        finalCancellationType = "partial";
      } else {
        // Filter out business-approved items
        allItemIds =
          selectedCartItem.items?.map((item) => item._id).filter(id => !businessApprovedItemIds.includes(id)) || [];
        totalQuantity = selectedCartItem.quantity || 0;
      }
    }

    // Filter out business-approved items from selected items
    const validItemIds = cancellationType === "full" 
      ? allItemIds 
      : selectedItemIds.filter(id => !businessApprovedItemIds.includes(id));

    const cancellationObject = {
        cartItemId: selectedCartItem._id,
      cancellationType: finalCancellationType,
      quantityToCancel:
        cancellationType === "full"
          ? validItemIds.length
          : validItemIds.length,
        reason: reason.trim(),
      itemIds: validItemIds,
          ...(selectedCartItem.variantEnabled && {
        variantName: selectedCartItem.variants?.[0]?.variantName,
      }),
    };

    console.log("=====> CancellationDialog - Data going to OrderDetailsDialog:", cancellationObject);
    onSubmit?.(cancellationObject);
  };

  const hasCustomizations = (item) =>
    item.customizations?.ingredientModifications?.some(
      (mod) => mod.action !== "no_modification"
    ) || false;

  const getCustomizationDetails = (item) => {
    if (!item.customizations?.ingredientModifications) return null;

    const actionMap = {
      substitute: (mod) =>
        `substitute ${mod.ingredientName} with ${mod.substituteWith}`,
      remove: (mod) => `remove ${mod.ingredientName}`,
      add: (mod) => `add extra ${mod.ingredientName}`,
    };

    const modifications = item.customizations.ingredientModifications
      .filter((mod) => mod.action !== "no_modification")
      .map((mod) => actionMap[mod.action]?.(mod))
      .filter(Boolean);

    return modifications.length > 0 ? modifications : null;
  };

  const handleClose = () => !isLoading && onClose();

  const renderCustomizationIcon = (customizationDetails) => (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1, textTransform: "lowercase" }}
          >
            customizations:
          </Typography>
          {customizationDetails.map((detail, idx) => (
            <Typography
              key={idx}
              variant="caption"
              sx={{ display: "block", textTransform: "lowercase", mb: 0.5 }}
            >
              • {detail}
            </Typography>
          ))}
        </Box>
      }
      arrow
      placement="top"
      PopperProps={{ style: { zIndex: 10002 } }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "white",
            color: "black",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "& .MuiTooltip-arrow": {
              color: "white",
              "&::before": { border: "1px solid #e0e0e0" },
            },
          },
        },
      }}
    >
      <IconButton size="small" sx={{ p: 0.5 }}>
        <CustomizeIcon size="16" color={PRIMARY_COLOR} />
      </IconButton>
    </Tooltip>
  );

  const renderItemCheckbox = (item, itemNumber, additionalInfo = "") => {
    const isCustomized = hasCustomizations(item);
    const customizationDetails = getCustomizationDetails(item);
    const isBusinessApproved = businessApprovedItemIds.includes(item._id);

    // Get the specific status for this item to determine the display text
    const getItemStatus = () => {
      if (selectedCartItem.variantEnabled && selectedCartItem.variants) {
        for (const variant of selectedCartItem.variants) {
          const variantItem = variant.items?.find(vi => vi.itemNumber === itemNumber);
          if (variantItem?.cancellation?.requested) {
            return variantItem.cancellation.status;
          }
        }
      } else {
        const subItem = selectedCartItem.items?.find(si => si.itemNumber === itemNumber);
        if (subItem?.cancellation?.requested) {
          return subItem.cancellation.status;
        }
      }
      return null;
    };

    const itemStatus = getItemStatus();
    const getStatusText = () => {
      if (itemStatus === "approved") {
        return " (already cancelled by business)";
      } else if (itemStatus === "pending_approval") {
        return " (already cancelled by customer)";
      }
      return "";
    };

    const getTooltipText = () => {
      if (itemStatus === "approved") {
        return "This item has already been cancelled by the business and approved. You cannot modify this cancellation.";
      } else if (itemStatus === "pending_approval") {
        return "This item has already been cancelled by the customer and is pending business approval. You cannot modify this cancellation.";
      }
      return "";
    };

    const checkboxControl = (
      <Box key={item._id} sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedItemIds.includes(item._id)}
              onChange={() => handleItemIdToggle(item._id)}
              disabled={isBusinessApproved}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  textTransform: "lowercase",
                  textDecoration: isBusinessApproved ? "line-through" : "none",
                  color: isBusinessApproved ? "text.secondary" : "inherit",
                  opacity: isBusinessApproved ? 0.6 : 1
                }}
              >
                item #{itemNumber}
                {additionalInfo}
                {isBusinessApproved && getStatusText()}
              </Typography>
              {isCustomized &&
                customizationDetails &&
                renderCustomizationIcon(customizationDetails)}
            </Box>
          }
        />
      </Box>
    );

    // Wrap with tooltip if business-approved
    if (isBusinessApproved) {
      return (
        <Tooltip
          key={item._id}
          title={getTooltipText()}
          arrow
          placement="top"
          PopperProps={{ style: { zIndex: 10002 } }}
        >
          {checkboxControl}
        </Tooltip>
      );
    }

    return checkboxControl;
  };

  const getTotalQuantity = () => {
    if (selectedCartItem.variantEnabled) {
      return (
        selectedCartItem.variants?.reduce(
          (total, variant) =>
            total +
            (variant.items?.reduce(
              (variantTotal, item) => variantTotal + (item.quantity || 0),
              0
            ) || 0),
          0
        ) || 0
      );
    }
    return selectedCartItem.quantity;
  };

  if (!selectedCartItem) return null;

  return (
    <CustomOverlay>
      <CustomDialog>
        <DialogHeader>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.25rem",
                textTransform: "lowercase",
              }}
            >
              cancel items
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textTransform: "lowercase" }}
            >
              {selectedCartItem.menuItemId.name} - quantity:{" "}
              {getTotalQuantity()}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: PRIMARY_COLOR,
              backgroundColor: PRIMARY_LIGHT,
              "&:hover": {
                backgroundColor: PRIMARY_COLOR,
                color: "white",
                transform: "scale(1.05)",
              },
              transition: TRANSITION,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        <ContentContainer>
          <ScrollableContent>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 700, textTransform: "lowercase" }}
              >
                cancellation type
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={cancellationType}
                  onChange={(e) => setCancellationType(e.target.value)}
                >
                  <FormControlLabel 
                    value="full" 
                    control={<Radio />} 
                    label="cancel entire item (all quantities)" 
                    sx={{ textTransform: "lowercase" }}
                  />
                  <FormControlLabel 
                    value="partial" 
                    control={<Radio />} 
                    label="cancel specific item numbers" 
                    sx={{ textTransform: "lowercase" }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {cancellationType === "partial" && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 700, textTransform: "lowercase" }}
                >
                  {selectedCartItem.variantEnabled
                    ? "select variants to cancel"
                    : "select items to cancel"}
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 2, textTransform: "lowercase" }}
                >
                  {selectedCartItem.variantEnabled 
                    ? "select specific items within the variant to cancel" 
                    : "select specific item numbers to cancel"}
                </Alert>
                <FormGroup>
                  {selectedCartItem.variantEnabled
                    ? selectedCartItem.variants
                        .map((variant) => {
                          const variantQuantity =
                            variant.items?.reduce(
                              (total, item) => total + (item.quantity || 0),
                              0
                            ) || 0;
                      if (variantQuantity === 0) return null;
                      
                          const variantPrice =
                            variant.items?.[0]?.variantPrice || 0;
                      
                      const itemCurrency = getItemCurrency(variant.items?.[0]);
                      return (
                        <Box key={variant.variantName} sx={{ mb: 2 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 1,
                                  fontWeight: 600,
                                  textTransform: "lowercase",
                                  color: PRIMARY_COLOR,
                                }}
                              >
                                {variant.variantName} variant • qty:{" "}
                                {variantQuantity} • {formatPrice(variantPrice, itemCurrency)} each
                              </Typography>
                              {variant.items?.map((item) => {
                                const currency = getItemCurrency(item);
                                return renderItemCheckbox(
                                  item,
                                  item.itemNumber,
                                  ` • qty: ${item.quantity} • ${formatPrice(item.variantPrice || 0, currency)} each`
                                );
                              })}
                            </Box>
                          );
                        })
                        .filter(Boolean)
                    : selectedCartItem.items.map((item) =>
                        renderItemCheckbox(item, item.itemNumber)
                  )}
                </FormGroup>
                {errors.itemIds && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block", textTransform: "lowercase" }}
                  >
                    {errors.itemIds}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 700, textTransform: "lowercase" }}
              >
                reason for cancellation (optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="please provide a reason for cancelling these items..."
                variant="outlined"
                sx={{ "& .MuiInputBase-input": { textTransform: "lowercase" } }}
              />
            </Box>
          </ScrollableContent>

          <BottomActions>
            <ActionButton 
              className="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              cancel
            </ActionButton>
            <ActionButton
              className="primary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              add to cancellations
            </ActionButton>
          </BottomActions>
        </ContentContainer>
      </CustomDialog>
    </CustomOverlay>
  );
};

export default CancellationDialog;